// File path: /shell/src/components/ModuleLoader.tsx
// Module Loader - Dynamically loads and renders module content

import React, { useEffect, useState, Suspense } from 'react';
import { Routes, Route, Navigate, useParams } from 'react-router-dom';
import { useModule } from '../context/ModuleContext';
import { useSubscription } from '@core/context/SubscriptionContext';
import { useAuth } from '@core/context/AuthContext';
import type { ModuleId } from '@core/config/moduleRegistry';
import type { ModuleEntryPoint, ModuleRouteConfig } from '@core/types/module';
import { effectiveRole } from '../routing/landingPath';

// Loading component for routes
const RouteLoadingFallback: React.FC = () => (
  <div className="flex items-center justify-center min-h-[200px]">
    <div className="module-loading-spinner" />
  </div>
);

// Error component for module load failures
const ModuleError: React.FC<{ moduleId: string; error?: Error }> = ({ moduleId, error }) => (
  <div className="max-w-2xl mx-auto mt-8 p-6 bg-red-50 border border-red-200 rounded-lg">
    <h2 className="text-lg font-semibold text-red-800 mb-2">Failed to Load Module</h2>
    <p className="text-red-600 mb-4">
      Unable to load the {moduleId} module. Please try again or contact support.
    </p>
    {error && (
      <pre className="text-sm text-red-500 bg-red-100 p-2 rounded overflow-auto">
        {error.message}
      </pre>
    )}
    <button
      onClick={() => window.location.reload()}
      className="mt-4 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
    >
      Reload Page
    </button>
  </div>
);

// Access denied component: the organization lacks the module, or the user's role has no page in it
const AccessDenied: React.FC<{ moduleId: string; reason?: 'subscription' | 'role' }> = ({
  moduleId,
  reason = 'subscription',
}) => (
  <div className="max-w-2xl mx-auto mt-8 p-6 bg-yellow-50 border border-yellow-200 rounded-lg">
    <h2 className="text-lg font-semibold text-yellow-800 mb-2">Access Denied</h2>
    {reason === 'role' ? (
      <p className="text-yellow-700">
        Your role does not include a page in the {moduleId} module.
        Please contact your administrator if you need access.
      </p>
    ) : (
      <p className="text-yellow-700">
        Your organization does not have access to the {moduleId} module.
        Please contact your administrator to request access.
      </p>
    )}
  </div>
);

// Whether the user's role or groups may open a route (no allowedRoles = everyone)
const canOpenRoute = (route: ModuleRouteConfig, user: any): boolean => {
  if (!route.allowedRoles || route.allowedRoles.length === 0) return true;
  const userRole = user?.role || 'Teacher';
  const userGroups = user?.groups || [];
  return route.allowedRoles.some(role =>
    role.toLowerCase() === userRole.toLowerCase() ||
    userGroups.some((g: string) => g.toLowerCase() === role.toLowerCase())
  );
};

// Render a single module route
const ModuleRoute: React.FC<{
  route: ModuleRouteConfig;
  moduleId: ModuleId;
}> = ({ route, moduleId }) => {
  const { user } = useAuth();
  const { hasFeatureAccess } = useSubscription();

  // Check role access
  if (!canOpenRoute(route, user)) {
    return <Navigate to={`/${moduleId}`} replace />;
  }

  // Check feature access
  if (route.featureId && !hasFeatureAccess(moduleId, route.featureId)) {
    return (
      <div className="max-w-2xl mx-auto mt-8 p-6 bg-gray-50 border border-gray-200 rounded-lg">
        <h2 className="text-lg font-semibold text-gray-800 mb-2">Feature Not Available</h2>
        <p className="text-gray-600">
          This feature requires an upgrade to your subscription plan.
        </p>
      </div>
    );
  }

  const Element = route.element;
  return (
    <Suspense fallback={<RouteLoadingFallback />}>
      <Element />
    </Suspense>
  );
};

// Main ModuleLoader component
const ModuleLoader: React.FC = () => {
  const { moduleId } = useParams<{ moduleId: string }>();
  const { loadModule, setActiveModule, getModuleEntryPoint, loadedModules } = useModule();
  const { hasModuleAccess, isLoading: subscriptionLoading, error: subscriptionError } = useSubscription();
  const { user } = useAuth();

  const [entryPoint, setEntryPoint] = useState<ModuleEntryPoint | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  // Validate module ID
  const validModuleId = moduleId as ModuleId;

  useEffect(() => {
    // The error belongs to the module now on screen: a failure for the previous one (e.g. a
    // loader-less module) must not follow the user to the next (C-MODERR).
    setError(null);

    if (!validModuleId) {
      setError(new Error('No module ID provided'));
      setLoading(false);
      return;
    }

    // Check module access first
    if (!hasModuleAccess(validModuleId)) {
      setLoading(false);
      return;
    }

    // Set active module
    setActiveModule(validModuleId);

    // Check if already loaded
    const cached = getModuleEntryPoint(validModuleId);
    if (cached) {
      setEntryPoint(cached);
      setLoading(false);
      return;
    }

    // Load the module
    setLoading(true);
    setError(null);

    loadModule(validModuleId)
      .then(ep => {
        setEntryPoint(ep);
        setLoading(false);
      })
      .catch(err => {
        console.error(`Failed to load module ${validModuleId}:`, err);
        setError(err);
        setLoading(false);
      });
  }, [validModuleId, loadModule, setActiveModule, getModuleEntryPoint, hasModuleAccess]);

  // Show loading state (also while the user's subscription is still loading: nothing is granted
  // yet, so "no access" would be premature)
  if (loading || subscriptionLoading) {
    return <RouteLoadingFallback />;
  }

  // The subscription call failed: say so, rather than "your organization has no access"
  if (subscriptionError) {
    return <ModuleError moduleId={validModuleId} error={new Error(subscriptionError)} />;
  }

  // Check module access
  if (!hasModuleAccess(validModuleId)) {
    return <AccessDenied moduleId={validModuleId} />;
  }

  // Show error state
  if (error || !entryPoint) {
    return <ModuleError moduleId={validModuleId} error={error || undefined} />;
  }

  // Get user role for default route
  const getUserRole = () => {
    const roleHierarchy = ['SuperAdmin', 'StateAdmin', 'ProgramAdmin', 'DistrictAdmin', 'SchoolAdmin', 'Coach', 'Teacher'];
    if (user?.role) {
      const roleMatch = roleHierarchy.find(r => r.toLowerCase() === user.role.toLowerCase());
      if (roleMatch) return roleMatch.toLowerCase();
    }
    if (user?.groups && user.groups.length > 0) {
      for (const role of roleHierarchy) {
        const groupMatch = user.groups.find((g: string) => g.toLowerCase() === role.toLowerCase());
        if (groupMatch) return role.toLowerCase();
      }
    }
    return 'teacher';
  };

  // Default page: the module's own page for the user's actual role (D-D — Navigate has no page
  // called schooladmin/districtadmin/superadmin/coach; the same role sign-in uses, so Counselor,
  // MTSSCoordinator and SpEdDirector get their own pages, not the switch's 'teacher'), else the
  // page named after the switch's role, as before — whichever this user may open. Elevate's
  // getDefaultRoute maps each of its roles to itself. Neither → the Access Denied view:
  // redirecting to a page that refuses the user sends them straight back here, an endless loop
  // (Ruling 24b).
  const userRole = getUserRole();
  const openable = (path?: string) => {
    const route = entryPoint.routes.find(r => r.path === path);
    return route && canOpenRoute(route, user) ? route.path : undefined;
  };
  const actualRole = effectiveRole(user?.role, user?.groups) ?? userRole;
  const defaultRoute = openable(entryPoint.getDefaultRoute?.(actualRole)) ?? openable(userRole);
  const landing = defaultRoute
    ? <Navigate to={defaultRoute} replace />
    : <AccessDenied moduleId={validModuleId} reason="role" />;

  // Render module routes
  return (
    <Routes>
      {/* Index route - redirect to default */}
      <Route index element={landing} />

      {/* Module routes */}
      {entryPoint.routes.map((route) => (
        <Route
          key={route.path}
          path={route.path}
          element={<ModuleRoute route={route} moduleId={validModuleId} />}
        />
      ))}

      {/* Catch-all for unmatched routes within module */}
      <Route path="*" element={landing} />
    </Routes>
  );
};

export default ModuleLoader;
